import React, { useEffect, useImperativeHandle, forwardRef, useMemo, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { deltaToPmJson, pmJsonToDelta } from 'lib/value'
import { KnovMention } from './tiptap/MentionExtension'
import { createMentionSuggestion } from './tiptap/MentionSuggestion'
import { MemeExtension } from './tiptap/MemeExtension'
import { ImageExtension } from './tiptap/ImageExtension'
import { VideoExtension } from './tiptap/VideoExtension'
import { ActionTagExtension } from './tiptap/ActionTagExtension'
import { MoreButtonExtension } from './tiptap/MoreButtonExtension'
import { ClearMarksPlugin } from './tiptap/ClearMarksPlugin'
import { extractUrls } from 'components/shared/LinkPreview'
import { debounce, uniq } from 'lodash'
import { generateFingerprint } from '../../utils/index'
import api from '../../api/api'
import { getCenterIndex, getSwiperRef } from 'state/imperativeApis/swiperApi'
import { isMobile } from 'react-device-detect'
import useStore from 'state/knovStore'
import { Extension } from '@tiptap/core'
import useUserHasFeature from '../../refactor/hooks/useUserHasFeature'
import useSearchHeaderHighlight from '../../refactor/hooks/useSearchHeaderHighlight'
import { v4 as uuid } from 'uuid'

// Import styles
import styles from './tiptap-editor.module.scss'
import fallbackMentionStyles from './mentions.module.scss'

// Define the TiptapEditor component interface to match QuillEditor
interface TiptapEditorProps {
    defaultValue?: any
    readOnly?: boolean
    placeholder?: string
    onChange?: (delta: any, oldContents: any, source: string) => void
    onTextChange?: (delta: any, oldContents: any, source: string) => void
    onSelectionChange?: (range: any, oldRange: any, source: string) => void
    onKeyDown?: (editor: any, event: KeyboardEvent) => void
    onFocus?: (editor: any) => void
    onBlur?: (editor: any) => void
    onEditorLoad?: (editor: any) => void
    style?: React.CSSProperties
    theme?: string | null
    lastKeyPressed?: string
    programmaticChangeRef?: React.MutableRefObject<boolean>
    // Mention system props
    users?: any[]
    models?: any[]
    setAgentModel?: (model: string | null) => void
    // Link handling props
    setLinkPreviews?: (links: string[]) => void
    setEmbeds?: (embeds: any[]) => void
    setStreamEmbeds?: (embeds: any[]) => void
    linkPreviewsFingerprint?: string
    embedsFingerprint?: string
    streamEmbedsFingerprint?: string
    answer?: any
    // CSS styles prop
    mentionStyles?: any
    // Image/Video handling props
    imgHandler?: (event: any) => void
    videoHandler?: (video: File) => void
    type?: string
    postHandler?: () => void
    [key: string]: any
}

// Define the ref interface
interface TiptapEditorRef {
    getEditor: () => any
    getContents: () => any
    setContents: (delta: any, source?: string) => void
    getText: () => string
    getLength: () => number
    getSelection: () => any
    setSelection: (index: number, length?: number, source?: string) => void
    focus: () => void
    enable: (enabled?: boolean) => void
    hasFocus: () => boolean
    getJSON: () => any
    commands: any
}

const TiptapEditor = forwardRef<TiptapEditorRef, TiptapEditorProps>(
    (
        {
            defaultValue,
            type,
            readOnly = false,
            placeholder = 'Type here...',
            onChange,
            onTextChange,
            onSelectionChange,
            onKeyDown,
            onFocus,
            onBlur,
            onEditorLoad,
            style,
            theme,
            lastKeyPressed,
            programmaticChangeRef,
            users,
            models,
            setAgentModel,
            setLinkPreviews,
            setEmbeds,
            setStreamEmbeds,
            linkPreviewsFingerprint,
            embedsFingerprint,
            streamEmbedsFingerprint,
            answer,
            mentionStyles,
            imgHandler,
            videoHandler,
            postHandler,
            ...otherProps
        },
        ref,
    ) => {
        const hasMobileShiftEnterFeature = useUserHasFeature('mobile_shift_enter_to_post')

        // Link handling refs - similar to CommonEditor
        const handleLinksRef = React.useRef(null)
        const handleKnovQuestLinksRef = React.useRef(null)
        const handleStreamLinksRef = React.useRef(null)

        // Track previous model mention state
        const previousHasModelMentionRef = React.useRef<boolean>(false)

        // ------------------------------------------------------------------
        // Live refs for users & models
        // ------------------------------------------------------------------
        const usersRef = useRef(users)
        const modelsRef = useRef(models)

        // Get the search header highlight function
        const highlightSearchHeader = useSearchHeaderHighlight()

        // Keep refs updated when props change (runs on every render if array ref changes)
        useEffect(() => {
            usersRef.current = users
        }, [users])

        useEffect(() => {
            modelsRef.current = models
        }, [models])

        // Create debounced link handling functions
        React.useEffect(() => {
            // Create the debounced function for external links
            handleLinksRef.current = debounce(
                nonKnovLinks => {
                    const editorFingerprint = generateFingerprint(nonKnovLinks)
                    const hasChanges = linkPreviewsFingerprint !== editorFingerprint

                    // Only update if links have actually changed
                    if (hasChanges && setLinkPreviews) {
                        setLinkPreviews(uniq(nonKnovLinks))
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Create debounced function for KnovQuest links
            handleKnovQuestLinksRef.current = debounce(
                async knovEmbedLinks => {
                    const shortCircuit =
                        answer?.is_transcription ||
                        answer?.mp4_recording_url ||
                        answer?.recording_url
                    if (shortCircuit) return // don't do this for transcriptions

                    const editorFingerprint = generateFingerprint(knovEmbedLinks)
                    const hasChanges = embedsFingerprint !== editorFingerprint

                    if (hasChanges) {
                        // convert quest URLs to obscureIds, and then request quests from backend
                        const questPromises = knovEmbedLinks
                            ?.map(link => (link || '')?.split('/').pop())
                            ?.map(api.getQuest)

                        // and finally add the embedded quests to the draft
                        return Promise.all(questPromises).then(embeds => {
                            embeds = embeds.map(e => e?.parent).filter(e => !!e)
                            setEmbeds?.(embeds)
                        })
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Create debounced function for Stream links
            handleStreamLinksRef.current = debounce(
                async streamEmbedLinks => {
                    if (!streamEmbedLinks || streamEmbedLinks.length === 0) return

                    const shortCircuit =
                        answer?.is_transcription ||
                        answer?.mp4_recording_url ||
                        answer?.recording_url
                    if (shortCircuit) return // don't do this for transcriptions

                    const editorFingerprint = generateFingerprint(streamEmbedLinks)
                    const hasChanges = streamEmbedsFingerprint !== editorFingerprint

                    if (hasChanges && setStreamEmbeds) {
                        // Extract stream IDs from URLs and create stream embed data
                        const streamEmbeds = streamEmbedLinks
                            ?.map(link => {
                                const streamId = (link || '')?.split('/').pop()
                                if (streamId) {
                                    return {
                                        id: streamId,
                                        type: 'stream',
                                        url: link,
                                    }
                                }
                                return null
                            })
                            ?.filter(e => !!e)

                        setStreamEmbeds(streamEmbeds)
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Cleanup function
            return () => {
                if (handleLinksRef.current) {
                    handleLinksRef.current.cancel()
                }
                if (handleKnovQuestLinksRef.current) {
                    handleKnovQuestLinksRef.current.cancel()
                }
                if (handleStreamLinksRef.current) {
                    handleStreamLinksRef.current.cancel()
                }
            }
        }, [
            linkPreviewsFingerprint,
            embedsFingerprint,
            streamEmbedsFingerprint,
            answer,
            setLinkPreviews,
            setEmbeds,
            setStreamEmbeds,
        ])

        // Convert Delta JSON to ProseMirror JSON for initial content
        const initialContent = React.useMemo(() => {
            if (!defaultValue) {
                return { type: 'doc', content: [{ type: 'paragraph' }] }
            }

            // If it's already a string, use it directly
            if (typeof defaultValue === 'string') {
                return defaultValue
            }

            // If it's a Delta JSON object, convert it
            try {
                const pmJson = deltaToPmJson(defaultValue)

                // Ensure we have valid content
                if (!pmJson || !pmJson.content || pmJson.content.length === 0) {
                    return { type: 'doc', content: [{ type: 'paragraph' }] }
                }

                return pmJson
            } catch (error) {
                console.warn('Failed to convert Delta to ProseMirror JSON:', error)
                return { type: 'doc', content: [{ type: 'paragraph' }] }
            }
        }, [defaultValue])

        // Memoize extensions to prevent unnecessary re-creation
        const extensions = useMemo(() => {
            const currentMentionStyles = mentionStyles || fallbackMentionStyles
            const memeClass = currentMentionStyles?.meme

            // Helper function to process uploaded files through imgHandler
            const handleImageUpload = (file: File) => {
                if (imgHandler) {
                    // Create a synthetic event object similar to what QuillEditor creates
                    const syntheticEvent = {
                        preventDefault: () => {},
                        stopPropagation: () => {},
                        target: { files: [file] },
                    }
                    imgHandler(syntheticEvent)
                }
            }

            const handleVideoUpload = (file: File) => {
                if (videoHandler) {
                    videoHandler(file)
                }
            }

            return [
                StarterKit.configure({
                    // Keep hardBreak enabled for desktop line breaks
                    hardBreak: {
                        // Only disable on mobile where we handle it manually
                        HTMLAttributes: {},
                    },
                    // Configure list behavior
                    bulletList: {
                        keepMarks: false,
                        keepAttributes: false,
                        HTMLAttributes: {
                            class: 'tiptap-bullet-list',
                        },
                    },
                    orderedList: {
                        keepMarks: false,
                        keepAttributes: false,
                        HTMLAttributes: {
                            class: 'tiptap-ordered-list',
                        },
                    },
                    listItem: {
                        HTMLAttributes: {
                            class: 'tiptap-list-item',
                        },
                    },
                }),
                Link.configure({
                    openOnClick: readOnly, // Enable link clicking only in read-only mode
                    HTMLAttributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                    },
                }),
                KnovMention.configure({
                    HTMLAttributes: {},
                    mentionClass: currentMentionStyles?.mention,
                    userMentionClass: currentMentionStyles?.userMention,
                    modelMentionClass: currentMentionStyles?.modelMention,
                    // Use getter functions to ensure suggestions always access the latest props data
                    // This prevents race conditions where the editor mounts before users/models are loaded
                    // The getter functions create a closure over the component scope, not the useMemo scope,
                    // so they always reference the current prop values when called
                    suggestion: createMentionSuggestion(
                        () => usersRef.current || [],
                        () => modelsRef.current || [],
                        setAgentModel,
                    ),
                    onMentionClick: readOnly
                        ? (mentionId: string, event: Event, element: HTMLElement) => {
                              // Find the panel context from the closest answer component
                              const answerComp = element.closest('.answer-comp') as HTMLElement
                              const panelId = answerComp?.dataset?.panelId
                              const editMode = answerComp?.dataset?.editMode === 'true'

                              // Don't open panel if in edit mode
                              if (editMode) return

                              if (panelId && mentionId) {
                                  // Get panel store methods
                                  const panels = useStore.getState().panels
                                  const panelToRight = panels.getPanelToRight(panelId)
                                  const targetFilter = { filter: { user: mentionId } }

                                  // Check if the right panel already has the user filter
                                  if (
                                      panelToRight &&
                                      panelToRight.filter?.user === mentionId &&
                                      !panelToRight.filter?.questId
                                  ) {
                                      // Panel already shows this user's stream
                                      const swiper = getSwiperRef().current
                                      const currentIndex = panels.getIndexById(panelId)
                                      const rightPanelIndex = panels.getIndexById(
                                          panelToRight.panelId,
                                      )

                                      // Check if we need to slide to the panel
                                      const needsSlide = isMobile || currentIndex > getCenterIndex()

                                      if (needsSlide && swiper) {
                                          // Edge case 1 (mobile) or Edge case 2 (desktop right panel)
                                          // Need to slide first, then shake and highlight
                                          swiper.once(
                                              'slideChangeTransitionEnd',
                                              function afterSlide() {
                                                  // Now that slide is complete, shake and highlight
                                                  panelToRight.shake()
                                                  highlightSearchHeader(panelToRight.panelId)
                                              },
                                          )

                                          // Slide to the next panel (one panel to the right)
                                          swiper.slideNext()
                                      } else {
                                          // Desktop center panel: Just shake and highlight immediately
                                          panelToRight.shake()
                                          highlightSearchHeader(panelToRight.panelId)
                                      }
                                  } else {
                                      // Determine animation - same logic as meme implementation
                                      const indexBeforeChange = panels.getIndexById(panelId)
                                      const clickedRightPanel = indexBeforeChange > getCenterIndex()

                                      // Generate panel ID upfront
                                      const newPanelId = uuid()

                                      // Insert panel to the right with our pre-generated ID
                                      panels.insertPanelRight(
                                          panelId,
                                          {
                                              ...targetFilter,
                                              panelId: newPanelId,
                                          },
                                          {
                                              animate: !isMobile && !clickedRightPanel,
                                          },
                                      )

                                      // Set up subscription to watch for our specific panel
                                      const unsubscribe = useStore.subscribe(state => {
                                          // Look for panel with our specific ID
                                          const newPanel = state.panels.state.find(
                                              p => p.panelId === newPanelId,
                                          )
                                          if (newPanel && !newPanel.empty) {
                                              // Panel is ready! Now check if we need to wait for slide
                                              const swiper = getSwiperRef().current
                                              const currentIndex = panels.getIndexById(panelId)
                                              const needsSlide =
                                                  isMobile || currentIndex !== getCenterIndex()

                                              if (needsSlide && swiper && swiper.animating) {
                                                  // Wait for slide to complete
                                                  swiper.once(
                                                      'slideChangeTransitionEnd',
                                                      function afterSlide() {
                                                          // Increased delay to ensure panel is fully rendered
                                                          setTimeout(() => {
                                                              newPanel.shake()
                                                              highlightSearchHeader(newPanelId)
                                                          }, 250)
                                                      },
                                                  )
                                              } else {
                                                  // No slide needed or not animating, shake with delay
                                                  setTimeout(() => {
                                                      newPanel.shake()
                                                      highlightSearchHeader(newPanelId)
                                                  }, 250)
                                              }

                                              unsubscribe() // Clean up subscription
                                          }
                                      })

                                      // Clean up subscription after timeout to prevent memory leaks
                                      setTimeout(() => {
                                          unsubscribe()
                                      }, 5000) // 5 second timeout
                                  }
                              }
                          }
                        : undefined,
                }),
                MemeExtension.configure({
                    HTMLAttributes: {},
                    memeClass,
                    onMemeClick: (memeContent: string, event: Event, element: HTMLElement) => {
                        // Find the panel context from the closest answer component
                        const answerComp = element.closest('.answer-comp') as HTMLElement
                        const panelId = answerComp?.dataset?.panelId
                        const editMode = answerComp?.dataset?.editMode === 'true'

                        // Don't open panel if in edit mode
                        if (editMode) return

                        if (panelId) {
                            // Get panel store methods
                            const panels = useStore.getState().panels

                            // Determine animation - same logic as old implementation
                            const indexBeforeChange = panels.getIndexById(panelId)
                            const clickedRightPanel = indexBeforeChange > getCenterIndex()

                            // Insert panel to the right with meme filter
                            panels.insertPanelRight(
                                panelId,
                                { filter: { all: true, meme: memeContent } },
                                { animate: !isMobile && !clickedRightPanel },
                            )
                        }
                    },
                }),
                ImageExtension.configure({
                    HTMLAttributes: {},
                    onImageUpload: handleImageUpload,
                }),
                VideoExtension.configure({
                    HTMLAttributes: {},
                    onVideoUpload: handleVideoUpload,
                }),
                ActionTagExtension,
                MoreButtonExtension.configure({
                    HTMLAttributes: {},
                    // The onClick handler is optional since it also dispatches the same event
                    // that existing code already listens for
                }),
                Placeholder.configure({
                    placeholder: placeholder,
                }),
                // Add ClearMarksPlugin to handle cursor style boundaries after deletion
                ClearMarksPlugin,
            ]
        }, [
            setAgentModel,
            mentionStyles,
            placeholder,
            imgHandler,
            videoHandler,
            onKeyDown,
            type,
            postHandler,
            readOnly,
        ])
        // Note: users and models are NOT in the dependencies array because we use getter functions
        // that always reference the current prop values when called. This prevents unnecessary
        // re-creation of extensions while ensuring suggestions always have access to the latest data.

        // @ts-ignore - TypeScript version conflict between different Tiptap packages
        const editor = useEditor({
            // @ts-ignore
            extensions,
            content: initialContent,
            editable: !readOnly,
            editorProps: {
                attributes: {
                    class: 'tiptap-prose',
                    style: 'outline: none;',
                    'data-placeholder': placeholder,
                },
                // Use handleDOMEvents instead of handleKeyDown to get proper DOM events with modifier keys on iOS
                handleDOMEvents: {
                    keydown: (view, event) => {
                        // Handle typing at the beginning of links to prevent link styling from bleeding
                        if (
                            event.key.length === 1 &&
                            !event.ctrlKey &&
                            !event.metaKey &&
                            !event.altKey
                        ) {
                            const { state } = view
                            const { selection } = state
                            const { $from } = selection

                            // Check if we're at the beginning of the document, start of a paragraph, or after a space
                            const isAtStart =
                                $from.pos === 1 || ($from.parentOffset === 0 && $from.pos > 1)

                            // Also check if we're right before a link (when typing at index 0 before a link)
                            let isBeforeLink = false
                            try {
                                const nodeAfter = $from.nodeAfter
                                if (nodeAfter && nodeAfter.marks) {
                                    isBeforeLink = nodeAfter.marks.some(
                                        mark => mark.type.name === 'link',
                                    )
                                }
                            } catch (e) {
                                // Ignore resolution errors
                            }

                            if (isAtStart || isBeforeLink) {
                                // Check if there are link marks at this position or stored marks
                                const currentMarks = $from.marks()
                                const storedMarks = state.storedMarks || []
                                const allMarks = [...currentMarks, ...storedMarks]

                                const hasLinkMark = allMarks.some(mark => mark.type.name === 'link')

                                if (hasLinkMark) {
                                    // Clear the link marks using ProseMirror transaction
                                    const linkMark = allMarks.find(
                                        mark => mark.type.name === 'link',
                                    )
                                    if (linkMark) {
                                        const tr = state.tr
                                        // Remove stored marks for future input
                                        tr.removeStoredMark(linkMark)
                                        // Apply the transaction
                                        view.dispatch(tr)
                                    }
                                }
                            }
                        }

                        // Handle space key specifically to clear link marks when creating separation
                        if (event.key === ' ') {
                            const { state } = view
                            const { selection } = state
                            const { $from } = selection

                            // Check if we have link marks that should be cleared
                            const currentMarks = $from.marks()
                            const storedMarks = state.storedMarks || []
                            const allMarks = [...currentMarks, ...storedMarks]

                            const linkMark = allMarks.find(mark => mark.type.name === 'link')

                            if (linkMark) {
                                // Check if we're at the end of a link (to prevent extending the link)
                                let isAtLinkEnd = false
                                try {
                                    const nodeBefore = $from.nodeBefore
                                    if (nodeBefore && nodeBefore.marks) {
                                        isAtLinkEnd = nodeBefore.marks.some(
                                            mark => mark.type.name === 'link',
                                        )
                                    }
                                } catch (e) {
                                    // Ignore resolution errors
                                }

                                if (isAtLinkEnd) {
                                    // Clear link marks to prevent extending the link with the space
                                    const tr = state.tr.removeStoredMark(linkMark)
                                    view.dispatch(tr)
                                }
                            }
                        }

                        if (onKeyDown && event.key === 'Enter') {
                            // For search type, always handle Enter
                            // For other types, handle based on mobile/desktop rules
                            const shouldHandle =
                                type === 'search' ||
                                (isMobile && hasMobileShiftEnterFeature) ||
                                event.metaKey ||
                                event.ctrlKey

                            if (shouldHandle) {
                                // Call the handler with the proper DOM event
                                const result = onKeyDown(editor, event)
                                // Convert Quill convention (false = handled) to Tiptap convention (true = handled)
                                if (result === false) {
                                    return true // In Tiptap, returning true prevents default
                                }
                            }
                        }
                        return false // Allow default behavior
                    },
                    click: (view, event) => {
                        const target = event.target as HTMLElement
                        const linkElement = target.closest('a[href]') as HTMLAnchorElement

                        if (linkElement?.href) {
                            const href = linkElement.getAttribute('href') || ''
                            const className = linkElement.getAttribute('class') || ''
                            const isMemeLink =
                                href.startsWith('@v2quest_self_') || className.includes('meme')

                            // Let MemeExtension handle meme links
                            if (isMemeLink) {
                                return false
                            }

                            // Handle regular links based on editor mode
                            event.preventDefault()
                            if (readOnly) {
                                // Open link in new tab when in read-only mode
                                window.open(linkElement.href, '_blank', 'noopener,noreferrer')
                            }
                            // In edit mode, prevent navigation (just prevent default)
                            return true
                        }

                        return false // Allow default behavior for non-links
                    },
                },
            },
            onUpdate: ({ editor: editorInstance, transaction }) => {
                // Check for model mention deletions only if this was a user action
                if (setAgentModel && !programmaticChangeRef?.current) {
                    try {
                        const pmJson = editorInstance.getJSON()
                        let currentHasModelMention = false

                        // Check if there are any model mentions in the document
                        const checkNode = (node: any): boolean => {
                            if (node.type === 'mention' && node.attrs?.denotationChar === '@@') {
                                currentHasModelMention = true
                                return true
                            }
                            if (node.content && Array.isArray(node.content)) {
                                return node.content.some(checkNode)
                            }
                            return false
                        }

                        if (pmJson.content) {
                            pmJson.content.some(checkNode)
                        }

                        // Only clear agent model if we had a model mention before and now we don't
                        // This indicates the user deleted it
                        if (previousHasModelMentionRef.current && !currentHasModelMention) {
                            console.log(
                                '[TiptapEditor] Model mention deleted by user, clearing agent model',
                            )
                            setAgentModel(null)
                        }

                        // Update the previous state for next comparison
                        previousHasModelMentionRef.current = currentHasModelMention
                    } catch (error) {
                        console.warn('Failed to check for model mentions:', error)
                    }
                }

                if (onTextChange) {
                    try {
                        // Convert current content back to Delta for compatibility
                        const pmJson = editorInstance.getJSON()
                        const delta = pmJsonToDelta(pmJson)
                        const oldContents = null // TODO: track previous state if needed
                        onTextChange(delta, oldContents, 'user')
                    } catch (error) {
                        console.warn('Failed to convert ProseMirror to Delta:', error)
                    }
                }

                // Extract and handle links - similar to CommonEditor
                try {
                    const currentText = editorInstance.getText()
                    const { knovEmbedLinks, nonKnovLinks, streamEmbedLinks } =
                        extractUrls(currentText)

                    // Handle links and embeds
                    handleKnovQuestLinksRef?.current?.(knovEmbedLinks)
                    handleLinksRef?.current?.(nonKnovLinks)
                    handleStreamLinksRef?.current?.(streamEmbedLinks)
                } catch (error) {
                    console.warn('Failed to process links:', error)
                }
            },
            onSelectionUpdate: ({ editor: editorInstance }) => {
                if (onSelectionChange) {
                    const selection = editorInstance.state.selection
                    const range = {
                        index: selection.from,
                        length: selection.to - selection.from,
                    }
                    onSelectionChange(range, null, 'user')
                }
            },
            onFocus: ({ editor: editorInstance }) => {
                if (onFocus) {
                    onFocus(editorInstance)
                }
            },
            onBlur: ({ editor: editorInstance }) => {
                if (onBlur) {
                    onBlur(editorInstance)
                }
            },
            onCreate: ({ editor: editorInstance }) => {
                // Initialize the model mention tracking
                try {
                    const pmJson = editorInstance.getJSON()
                    let hasModelMention = false

                    const checkNode = (node: any): boolean => {
                        if (node.type === 'mention' && node.attrs?.denotationChar === '@@') {
                            hasModelMention = true
                            return true
                        }
                        if (node.content && Array.isArray(node.content)) {
                            return node.content.some(checkNode)
                        }
                        return false
                    }

                    if (pmJson.content) {
                        pmJson.content.some(checkNode)
                    }

                    previousHasModelMentionRef.current = hasModelMention
                } catch (error) {
                    console.warn('Failed to initialize model mention tracking:', error)
                }

                // Pass the ref (with compatibility methods) instead of raw editor
                // This ensures calling code gets expected Quill-like API
                if (onEditorLoad) {
                    // Create a temporary ref-like object with all the methods
                    const compatibleEditor = {
                        getEditor: () => editorInstance,
                        getContents: () => {
                            if (!editorInstance) return { ops: [] }
                            try {
                                const pmJson = editorInstance.getJSON()
                                return pmJsonToDelta(pmJson)
                            } catch (error) {
                                console.warn('Failed to get contents:', error)
                                return { ops: [] }
                            }
                        },
                        setContents: (delta: any, source = 'api') => {
                            if (!editorInstance) return
                            try {
                                const pmJson = deltaToPmJson(delta)
                                // Use false to not emit update events, preventing loops
                                editorInstance.commands.setContent(pmJson, false)

                                // If this is setting a model mention, ensure cursor is positioned correctly
                                const hasModelMention = delta?.ops?.some(
                                    (op: any) => op?.insert?.mention?.denotationChar === '@@',
                                )
                                if (hasModelMention) {
                                    // Position cursor after the mention and space
                                    setTimeout(() => {
                                        editorInstance.commands.focus('end')
                                    }, 0)
                                }
                            } catch (error) {
                                console.warn('Failed to set contents:', error)
                            }
                        },
                        getText: () => editorInstance?.getText() || '',
                        getLength: () => editorInstance?.state.doc.nodeSize || 0,
                        getSelection: () => {
                            if (!editorInstance) return null
                            const selection = editorInstance.state.selection
                            return {
                                index: selection.from,
                                length: selection.to - selection.from,
                            }
                        },
                        setSelection: (index: number, length = 0, source = 'api') => {
                            if (!editorInstance) return
                            editorInstance.commands.setTextSelection({
                                from: index,
                                to: index + length,
                            })
                        },
                        focus: () => editorInstance?.commands.focus(),
                        enable: (enabled = true) => editorInstance?.setEditable(enabled),
                        hasFocus: () => editorInstance?.isFocused || false,
                        getJSON: () => editorInstance?.getJSON(),
                        commands: editorInstance?.commands,
                    }

                    onEditorLoad(compatibleEditor)
                }
            },
        })

        // Expose editor methods via ref to match QuillEditor API
        useImperativeHandle(
            ref,
            () => ({
                // Core editor access
                getEditor: () => editor,

                // Quill-compatible methods
                getContents: () => {
                    if (!editor) return { ops: [] }
                    try {
                        const pmJson = editor.getJSON()
                        return pmJsonToDelta(pmJson)
                    } catch (error) {
                        console.warn('Failed to get contents:', error)
                        return { ops: [] }
                    }
                },

                setContents: (delta: any, source = 'api') => {
                    if (!editor) return
                    try {
                        const pmJson = deltaToPmJson(delta)
                        // Use false to not emit update events, preventing loops
                        editor.commands.setContent(pmJson, false)

                        // If this is setting a model mention, ensure cursor is positioned correctly
                        const hasModelMention = delta?.ops?.some(
                            (op: any) => op?.insert?.mention?.denotationChar === '@@',
                        )
                        if (hasModelMention) {
                            // Position cursor after the mention and space
                            setTimeout(() => {
                                editor.commands.focus('end')
                            }, 0)
                        }
                    } catch (error) {
                        console.warn('Failed to set contents:', error)
                    }
                },

                getText: () => {
                    return editor?.getText() || ''
                },

                getLength: () => {
                    return editor?.state.doc.nodeSize || 0
                },

                getSelection: () => {
                    if (!editor) return null
                    const selection = editor.state.selection
                    return {
                        index: selection.from,
                        length: selection.to - selection.from,
                    }
                },

                setSelection: (index: number, length = 0, source = 'api') => {
                    if (!editor) return
                    editor.commands.setTextSelection({ from: index, to: index + length })
                },

                focus: () => {
                    editor?.commands.focus()
                },

                enable: (enabled = true) => {
                    editor?.setEditable(enabled)
                },

                hasFocus: () => {
                    return editor?.isFocused || false
                },

                // Tiptap-specific methods
                getJSON: () => editor?.getJSON(),
                commands: editor?.commands,
            }),
            [editor],
        )

        // Handle enable/disable when readOnly changes
        useEffect(() => {
            if (editor) {
                editor.setEditable(!readOnly)

                // Update placeholder when it changes
                editor.setOptions({
                    editorProps: {
                        attributes: {
                            class: 'tiptap-prose',
                            style: 'outline: none;',
                            'data-placeholder': placeholder,
                        },
                    },
                })

                // Add development helpers for testing media functionality
                if (process.env.NODE_ENV === 'development') {
                    ;(window as any).testTiptapImage = () => {
                        console.log('Adding test image...')
                        editor.commands.setImage({
                            src: 'https://via.placeholder.com/300x200/0066cc/ffffff?text=Test+Image',
                            alt: 'Test image',
                        })
                    }
                    ;(window as any).testTiptapVideo = () => {
                        console.log('Adding test video...')
                        editor.commands.setVideo({
                            src: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
                            controls: true,
                        })
                    }

                    // Only log helper info in development
                    // console.log('💡 TiptapEditor media helpers available:')
                    // console.log('  - testTiptapImage() - add a test image')
                    // console.log('  - testTiptapVideo() - add a test video')
                    // console.log('  - Try drag & drop images/videos into the editor!')
                    // console.log('  - Try pasting images from clipboard!')
                }
            }
        }, [editor, readOnly, mentionStyles, placeholder])

        // Helper function to get the appropriate CSS class based on type
        const getTypeClass = (type?: string) => {
            const currentMentionStyles = mentionStyles || fallbackMentionStyles

            switch (type) {
                case 'title':
                    return currentMentionStyles.title || ''
                case 'answer':
                    return currentMentionStyles.answer || ''
                case 'new-answer':
                    return currentMentionStyles.newAnswer || ''
                case 'search':
                    return currentMentionStyles.search || ''
                default:
                    return currentMentionStyles.answer || '' // Default to answer styling
            }
        }

        return (
            <div
                style={style}
                {...otherProps}
                className={`${styles.tiptapEditorContainer} tiptap-editor-container ${getTypeClass(
                    type,
                )} ${readOnly ? 'readonly' : ''}`}
                data-mention-editor="true"
            >
                <EditorContent editor={editor} className={`${styles.editorContent}`} />
            </div>
        )
    },
)

TiptapEditor.displayName = 'TiptapEditor'

export default TiptapEditor
